import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { msalInstance, tokenRequest } from '@/config/msalConfig';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
    public details?: Record<string, string[]>,
    public traceId?: string
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

function createApiClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const activeAccount = msalInstance.getActiveAccount();
      if (activeAccount) {
        try {
          const response = await msalInstance.acquireTokenSilent({
            ...tokenRequest,
            account: activeAccount,
          });
          config.headers.Authorization = `Bearer ${response.accessToken}`;
        } catch (error) {
          // Token acquisition failed — will retry on 401
        }
      }
      config.headers['X-Correlation-Id'] = crypto.randomUUID();
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError<{ message?: string; code?: string; details?: Record<string, string[]>; traceId?: string }>) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && originalRequest) {
        const activeAccount = msalInstance.getActiveAccount();
        if (activeAccount) {
          try {
            const response = await msalInstance.acquireTokenPopup(tokenRequest);
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
            return client.request(originalRequest);
          } catch {
            window.location.href = '/login';
            return Promise.reject(error);
          }
        }
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (error.response) {
        const apiError = error.response.data;
        throw new ApiRequestError(
          apiError?.message || error.message || 'An unexpected error occurred',
          error.response.status,
          apiError?.code,
          apiError?.details,
          apiError?.traceId
        );
      }

      if (error.request) {
        const detail = error.code ? `${error.message} [${error.code}]` : error.message;
        console.error('[apiClient] Network error detail:', error.code, error.message, 'URL:', error.config?.url);
        throw new ApiRequestError(`Network error: ${detail}`, 0, error.code || 'NETWORK_ERROR');
      }

      throw new ApiRequestError(error.message || 'An unexpected error occurred', 0, 'UNKNOWN_ERROR');
    }
  );

  return client;
}

export const api = createApiClient(import.meta.env.VITE_API_BASE_URL || '/api');
